import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Save, X, Loader2, CheckCircle2, AlertCircle, Factory } from 'lucide-react';
import { api } from '../../../lib/api';
import { useFactory } from '../../../context/FactoryContext';

const ConfigEmpresa = () => {
  
  const {selectedFactory, factories, factoryCodes } = useFactory();
  //const [searchParams, setSearchParams] = useSearchParams()
  const [assetDetails, setAssetDetails] = useState({
        nombre: '',
        nit: '',
        direccion: '',
        telefono: '',
        email: '',
        sitioWeb: '',
        ciudad: '',
        estado: '',
        codigoPostal: '',
        pais: '',
  });

  //Función que lea el Asset Code y devuelva su Asset ID.
  const getAssetIdFromSelectedFactory = () => {
    const returned = factories.find((factory) => factory.Code === selectedFactory || factory.asset_code === selectedFactory);
    //Probablemente haya que coger Phone y Nit de aquí en vez de entrar a detalles.
    return returned.ID || returned.id;
}

  //Función que devuelva el objeto json del assetId recogido
  const getAssetDetailsFromAssetId = async (assetId) => {
    const details = await api.get(`/api/asset/showdetail/${assetId}`);
    return details;
  }
  
  //Impresión en consola para debugging
  useEffect(() => {
    console.log(assetDetails);
  }, [assetDetails]);

  
  // Cargar información de la empresa por fábrica
  const { data: companyData, isLoading, isError, error } = useQuery({
    queryKey: ['company-info', selectedFactory],
    queryFn: async () => {
      if (!selectedFactory) {
        return null;
      }
      try {
        const fetchDetails = async () => {
          const id = getAssetIdFromSelectedFactory();
          const details = await getAssetDetailsFromAssetId(id);
          console.log(details);
          return details;
        }
        return fetchDetails();
      } catch (err) {
        console.error('Error loading company info:', err);
        throw err;
      }
    },
    enabled: !!selectedFactory,
    retry: 1,
  });

  // Cargar datos cuando se reciban del servidor o cambie la fábrica
  useEffect(() => {
    if (companyData) {
      const country_data = companyData.Product.FeatureValues.find((value) => value.Name === 'Country');
      const state_data = companyData.Product.FeatureValues.find((value) => value.Name === 'State');
      const city_data = companyData.Product.FeatureValues.find((value) => value.Name === 'City');
      const postcode_data = companyData.Product.FeatureValues.find((value) => value.Name === 'Postal Code');
      const address_data = companyData.Product.FeatureValues.find((value) => value.Name === 'Address');
      setAssetDetails(prev =>({
        ...prev,
        nombre: companyData?.Product?.Name || '',
        pais:country_data?.StringValue || '',
        estado:state_data?.StringValue || '',
        ciudad:city_data?.StringValue || '',
        codigoPostal: postcode_data?.StringValue || '',
        direccion: address_data?.StringValue || ''
      }));
    } else if (selectedFactory) {
      // Si hay fábrica pero no hay datos, resetear formulario
      setAssetDetails({
        nombre: '',
        nit: '',
        direccion: '',
        telefono: '',
        email: '',
        sitioWeb: '',
        ciudad: '',
        estado: '',
        codigoPostal: '',
        pais: '',
      });
    }
  }, [companyData, selectedFactory]);


  if (!selectedFactory) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-yellow-900/20 border border-yellow-500 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-yellow-500" size={24} />
          <div>
            <h3 className="text-yellow-400 font-semibold">Fábrica no seleccionada</h3>
            <p className="text-yellow-300 text-sm">Por favor, selecciona una fábrica para configurar su información</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-500" size={24} />
          <div>
            <h3 className="text-red-400 font-semibold">Error al cargar información</h3>
            <p className="text-red-300 text-sm">{error?.message || 'No se pudo cargar la información de la empresa'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="text-blue-500" size={32} />
          <h1 className="text-3xl font-bold text-white">
            Información de la Empresa
          </h1>
        </div>
        <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
          <span>Location: {assetDetails.pais}</span>
          <span>Code: {selectedFactory}</span>
        </div>
      </div>

      {/*Mensaje de exito eliminado */}

      {/* Formulario */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg">
        <div className="p-6">
          <div className="space-y-6">
            {/* Información Básica */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-gray-700">
                Información Básica
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nombre de la Empresa
                  </label>
                  {assetDetails.nombre === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails.nombre}
                    </p>
                  }
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    NIT / Número de Identificación
                  </label>
                  {assetDetails.nit === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails.nit}
                    </p>
                  }
                </div>
              </div>
            </div>

            {/* Información de Contacto */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-gray-700">
                Información de Contacto
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Teléfono
                  </label>
                  {assetDetails.telefono === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails.telefono}
                    </p>
                  }
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  {assetDetails.email === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails.email}
                    </p>
                  }
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sitio Web
                  </label>
                  {assetDetails.sitioWeb === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails.sitioWeb}
                    </p>
                  }
                </div>
              </div>
            </div>

            {/* Dirección */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-gray-700">
                Dirección
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Dirección
                  </label>
                  {assetDetails.direccion === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails.direccion}
                    </p>
                  }
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Ciudad
                  </label>
                  {assetDetails.ciudad === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails.ciudad}
                    </p>
                  }
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Estado / Provincia
                  </label>

                  {assetDetails.estado === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails.estado}
                    </p>
                  }
                  
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Código Postal
                  </label>
                  {assetDetails.codigoPostal === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails.codigoPostal}
                    </p>
                  }
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    País
                  </label>
                  {assetDetails.pais === '' ?
                    (<p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-500">
                      Indefinido
                    </p>) :
                    <p className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100">
                      {assetDetails?.pais}
                    </p>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigEmpresa;